<template>
  <div class="color-scheme color-scheme--dialog">
    <p class="store-locator--disclaimer text-sm" v-html="labels.disclaimer"></p>

    <label class="smallcaps text-sm" for="store-selector-choose-variant">
      <strong>
        {{ labels.choose_size }}
      </strong>
    </label>

    <div class="store-locator--choose-variant form-control">
      <select
        v-model="currentVariant"
        :disabled="loading || error !== null"
        id="store-selector-choose-variant"
        class="select w-full"
      >
        <option
          v-for="variant in filteredVariants"
          :key="variant.sku"
          :value="variant.sku"
        >
          {{ variant.name }}
        </option>
      </select>

      <svg
        aria-hidden="true"
        focusable="false"
        fill="none"
        width="10"
        class="icon icon-chevron-down"
        viewBox="0 0 10 6"
      >
        <path d="m1 1 4 4 4-4" stroke="currentColor" stroke-linecap="square" />
      </svg>
    </div>

    <div v-if="loading" class="store-locatore--loading">
      <div class="loader">{{ labels.loading }}</div>
    </div>

    <div
      v-if="!loading && selectedStores.length === 0 && error === null"
      class="store-locator--no-stores"
    >
      <p>{{ labels.no_stores_found }}</p>
    </div>

    <div v-if="error !== null">
      <p class="store-locator--error">
        {{ labels.error }}
      </p>
    </div>

    <div
      v-if="selectedStores.length > 0"
      class="store-locator--selected-stores"
    >
      <div
        v-for="store in selectedStores"
        :key="store.warehouseId"
        class="store-locator--store"
      >
        <a
          v-if="store.url"
          :href="store.url"
          class="store-locator--store__heading"
        >
          {{ store.address }}
        </a>
        <p v-else class="store-locator--store__heading">
          {{ store.address }}
        </p>

        <div class="h-stack justify-between align-start">
          <div class="v-stack justify-start align-start">
            <p>{{ store.zip }}, {{ store.city }}</p>
          </div>

          <p v-if="store.phone">
            {{ labels.phone }}
            <a class="store-locator--store__phone" :href="`tel:${store.phone}`">
              {{ store.phone }}
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StoreLocator',
  props: {
    endpoint: String,
    locale: String
  },
  data() {
    return {
      loading: true,
      parentSku: null,
      currentVariant: null,
      variants: [],
      filteredVariants: [],
      stores: [],
      selectedStores: [],
      labels: {},
      error: null
    };
  },
  watch: {
    parentSku(newParentSku, oldParentSku) {
      if (newParentSku === oldParentSku) {
        return;
      }

      this.getStores(newParentSku);
    },
    stores(newStores) {
      if (!newStores) {
        console.error('No stores available');
        return;
      }

      this.setSelectedStore(newStores, this.currentVariant);
    },
    currentVariant(newVariant, oldVariant) {
      if (newVariant === oldVariant) {
        return;
      }

      this.setSelectedStore(this.stores, newVariant);

      const filteredVariants = this.variants.filter(
        ({ sku }) =>
          sku.replace(/-[0-9]\.[a-z0-9]+$/i, '') ===
          newVariant.replace(/-[0-9]\.[a-z0-9]+$/i, '')
      );
      this.filteredVariants = filteredVariants;
    }
  },
  methods: {
    async getStores(sku) {
      this.loading = true;

      try {
        const response = await fetch(`${this.endpoint}?sku=${sku}`);
        const { variants } = await response.json();

        if (response.status !== 200) {
          throw new Error('Error fetching stores');
        }

        if (!variants) {
          throw new Error('No variants found');
        }

        this.stores = variants;
      } catch (e) {
        console.error(e.message);
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    setSelectedStore(stores, currentSku) {
      const selectedVariant = stores.find(({ sku }) => sku === currentSku);

      if (!selectedVariant) {
        this.selectedStores = [];
        return;
      }

      const updatedStores = selectedVariant.inventoryLevels.reduce(
        (acc, store) => {
          const storeInfo = window.warehouses.find(
            ({ code }) => code === store.warehouseId
          );

          return storeInfo ? [...acc, { ...store, ...storeInfo }] : acc;
        },
        []
      );

      this.selectedStores = updatedStores;
    }
  },
  mounted() {
    const _this = this;
    const storeLocatorDrawer = document.querySelector('#store-locator-drawer');

    this.labels = window.labels;

    this.currentVariant = window.currentProduct.initialVariant;
    this.variants = window.currentProduct.variants.sort(
      // sort by name
      ({ name: nameA }, { name: nameB }) =>
        nameA.localeCompare(nameB, _this.locale)
    );

    storeLocatorDrawer.addEventListener('dialog:after-show', () => {
      if (_this.stores.length > 0) {
        return;
      }

      this.parentSku =
        window.currentProduct.initialVariant.match(/[a-z0-9]{9}/i)[0];
    });

    document.addEventListener('variant:change', ({ detail }) => {
      _this.currentVariant = detail.variant.sku;
    });
  }
};
</script>
